import i18n from "@/i18n/index.js";
import { roundRect } from "../../../../utils/utils";
import { ContainerExpandableTriangle } from "./ContainerExpandableTriangle";
import { FabricRectangular } from "../../../../views/grid/components/FabricRectangular";
import { ClassProperty } from "./ClassProperty";
import { DATA_MODEL_PROPERTY_LABEL_STYLE } from "./config/property.config";
import {
    INVALID_CLASS_CONTAINER_STYLE, SELECTED_CLASS_CONTAINER_STYLE, DEFAULT_CLASS_CONTAINER_STYLE, CONTAINER_STYLE, CONTAINER_MORE_ITEMS_INDICATOR, SELECTED_CHILD_STROKE_COLOR
} from "./config/classContainer.config";
import { ClassText } from "./ClassText";
import { PropertyAddInstance } from "./PropertyAddInstance";
import { DISABLED_ELEMENT_OVERLAY_COLOR } from "../../../../views/grid/const/disabled-elements.const";

export class ClassContainer extends FabricRectangular {
    layout = {
        x: null, y: null, width: null, height: null
    };

    state = { selected: null, drawed: false };

    ctx;

    instances = {
        expandable: null,
        children: [],
        addInstance: []
    };

    labelStyle = DATA_MODEL_PROPERTY_LABEL_STYLE;

    childHeight = 30;

    addInstanceHeight = 0;

    eventEmitter;

    undrawedItemsInstance;

    moreItemsLayout = null;

    constructor(layout, state, ctx, data) {
        super(layout);
        this.layout = layout;
        this.state = state;
        this.ctx = ctx;
        this.instances.expandable = new ContainerExpandableTriangle(this.layout, this.state, this.ctx);
        if (this.state.readonly) this.addInstanceHeight = 0;
        this.instances.addInstance = new PropertyAddInstance({
            x: this.layout.x, y: this.layout.y, width: this.layout.width, height: this.addInstanceHeight
        }, { readonly: this.state.readonly }, this.ctx);
        if (data && data.children) {
            data.children.forEach((child) => {
                const property = new ClassProperty({
                    x: child.x, y: child.y, width: child.width, height: child.height
                }, null, this.ctx, { ...child.data, id: child.id, uuid: child.uuid });
                this.instances.children.push(property);
            });
        }
    }

    setEventEmitter(ev) {
        this.eventEmitter = ev;
    }

    draw() {
        this.state.drawed = true;
        let style = this.state.dragHover ? INVALID_CLASS_CONTAINER_STYLE
            : this.state.selected || this.state.active ? SELECTED_CLASS_CONTAINER_STYLE
                : DEFAULT_CLASS_CONTAINER_STYLE;
        style = { ...style };
        if (this.state.childSelected) style.strokeStyle = SELECTED_CHILD_STROKE_COLOR;
        const styleWithoutFill = { ...style };
        delete styleWithoutFill.fillStyle;
        const lineWidth = style.lineWidth || 1;

        this.ctx.clearRect(this.layout.x + 3, this.layout.y + lineWidth * 4, this.layout.width - (lineWidth * 2) - 2, this.layout.height - (lineWidth * 2) - 2);
        // lineWidth * 2
        roundRect(this.layout.x + 3, this.layout.y - 1, this.layout.width - 3, this.layout.height, CONTAINER_STYLE.radius, styleWithoutFill, this.ctx);
        this.getExpandableInstance().draw();
        if (!this.preview) {
            this.instances.addInstance.draw();
        }
        this.drawChildren();
        if (this.state.disabled) {
            const style = styleWithoutFill;
            style.fillStyle = DISABLED_ELEMENT_OVERLAY_COLOR;
            roundRect(this.layout.x + 1, this.layout.y + 1, this.layout.width - 1, this.layout.height - 1, CONTAINER_STYLE.radius, styleWithoutFill, this.ctx);
        }
    }

    getChildrenData() {
        const value = [];
        this.instances.children.forEach(child => {
            const val = {};
            const dataToSet = { ...child.data };
            if (dataToSet && dataToSet.hasOwnProperty("oldState")) {
                delete dataToSet.oldState;
            }
            val.id = dataToSet.id;
            val.uuid = child.uuid;
            val.x = child.layout.x;
            val.y = child.layout.y;
            val.width = child.layout.width;
            val.height = child.layout.height;
            delete dataToSet.id;
            val.data = dataToSet;
            value.push(val);
        });
        return value;
    }

    getChild(uuid) {
        return this.instances.children.find(child => child.uuid === uuid);
    }

    addChild(state, data, Factory, childPluginID) {
        const currentY = this.layout.y + (this.instances.children.length * this.childHeight) + this.instances.addInstance.layout.height;
        const childData = { ...data, id: childPluginID };
        const layout = {
            x: this.layout.x, y: currentY, width: this.layout.width, height: this.childHeight
        };
        const newInstance = new Factory(layout, state, this.ctx, childData);
        this.instances.children.push(newInstance);
        this.drawChildren();
        return newInstance;
    }

    updateChildData(containerUuid, childUuid, data) {
        const index = this.instances.children.findIndex(child => child.uuid === childUuid);
        if (index === -1) {
            // TODO RETURN ERROR
        }
        const child = this.instances.children[index];
        const childOldLabel = child.getPropertyLabel();
        child.update({}, {}, data);
        child.draw();
        child.emitUpdateLabelEvent(this.eventEmitter, containerUuid, childUuid, childOldLabel);
    }

    removeChild(childUuid) {
        const childIndex = this.instances.children.findIndex((child) => child.uuid === childUuid);
        if (childIndex === -1) {
            return false;
        }
        this.instances.children.splice(childIndex, 1);
        const addInstanceHeight = this.instances.addInstance.layout.height;
        this.instances.children.forEach((child, index) => {
            const currentY = this.layout.y + (index * this.childHeight) + addInstanceHeight;
            child.update({ x: this.layout.x, y: currentY });
        });
        this.draw();
        return true;
    }

    drawChildren() {
        let unDrawedCount = 0;
        const stopAt = (this.layout.y + this.layout.height) - this.getExpandableInstance().getOcupiedHeight();
        for (let i = 0; i < this.instances.children.length; i++) {
            const child = this.instances.children[i];
            if (child.layout.y + this.childHeight < stopAt && unDrawedCount === 0) {
                child.draw();
            } else if (unDrawedCount === 0) {
                unDrawedCount = this.instances.children.length - i;
            } else {
                child.update({}, { drawed: false });
            }
        }
        this.drawXMoreItems(unDrawedCount, stopAt);
    }

    clearSection(x, y, width, height, ctx) {
        ctx.clearRect(x, y, width, height);
    }

    drawXMoreItems(unDrawedCount, stopAt) {
        const height = this.getExpandableInstance().getOcupiedHeight();
        const fillTextMaxWidth = this.layout.width - this.labelStyle.paddingLeft * 3;
        const txt = unDrawedCount === 0 ? "" : `${unDrawedCount} more items!`;
        this.clearSection(this.layout.x + this.labelStyle.paddingLeft, stopAt, fillTextMaxWidth - this.getExpandableInstance().getOcupiedWidth(), height - 2, this.ctx);
        const layout = {
            x: this.layout.x, y: stopAt, width: this.layout.width, height
        };
        if (unDrawedCount) {
            this.moreItemsLayout = layout;
            this.moreItemsLayout.unDrawedCount = unDrawedCount;
        } else {
            this.moreItemsLayout = null;
        }
        const style = CONTAINER_MORE_ITEMS_INDICATOR;
        if (!this.undrawedItemsInstance) {
            this.undrawedItemsInstance = new ClassText(this.state, layout, style, this.ctx, txt);
        } else {
            this.undrawedItemsInstance.update(this.state, layout, null, txt);
        }
        this.undrawedItemsInstance.draw();
    }

    updateChildren(children) {
        this.children = children;
    }

    childrenClickCheck(payload) {
        const clickedAdd = this.instances.addInstance.pointInRect(payload);
        let clickedItem = false;
        for (let i = 0; i < this.instances.children.length; i++) {
            const child = this.instances.children[i];
            if (child.pointInRect(payload) && child.state.drawed) {
                clickedItem = child;
                break;
            }
        }
        const returnVal = clickedAdd ? this.instances.addInstance : clickedItem || null;
        return returnVal;
    }

    clickedInstanceBody(payload) {
        return this.pointInRect(payload);
    }

    getExpandableInstance() {
        return this.instances.expandable;
    }

    getMoreItemsLayout() {
        return this.moreItemsLayout;
    }

    update(layout, state) {
        if (layout) {
            this.layout = { ...this.layout, ...layout };
            this.setPoints(this.layout);
            const newAddLayout = {
                x: this.layout.x, y: this.layout.y, width: this.layout.width, height: this.addInstanceHeight
            };
            this.instances.addInstance.update(newAddLayout);
            this.instances.children.forEach((child, index) => {
                const currentY = this.layout.y + (index * this.childHeight) + this.instances.addInstance.layout.height;
                const newLayout = {
                    x: this.layout.x, y: currentY, width: this.layout.width, height: this.childHeight
                };
                child.update(newLayout);
            });
        }
        if (state) this.state = { ...this.state, ...state };
        const expandable = this.getExpandableInstance();
        if (expandable) expandable.update(this.layout, this.state);
    }

    showMoreItemsReadonly(payload) {
        const textarea = document.createElement("textarea"); let
            finalValue = "Show more items: \n";
        textarea.setAttribute("id", "moreItemsListId");
        textarea.style.position = "absolute";
        textarea.style.border = "1px solid #000000";
        textarea.style["line-height"] = "20px";
        textarea.style["flex-wrap"] = "nowrap";
        textarea.style.left = `${payload.event.x}px`;
        textarea.style.top = `${payload.event.y}px`;
        textarea.style.maxHeight = "150px";
        textarea.readOnly = "true";
        textarea.style["font-size"] = "14px";
        textarea.classList = "more-items-data-model";

        const data = this.instances.children;
        const key = data.length - this.moreItemsLayout.unDrawedCount;
        for (let i = key; i < data.length; i++) {
            let customValue = data[i].data.label[0].text + data[i].data.label[1].text;
            if (customValue.length > 20) {
                customValue = `${customValue.substr(0, 17)}...`;
            }
            finalValue += customValue + (i < data.length - 1 ? "\n" : "");
        }
        textarea.value = finalValue;
        textarea.addEventListener("keydown", this.itemLoseFocus);
        textarea.addEventListener("blur", this.itemLoseFocus);
        document.body.appendChild(textarea);
        this.textereaProps(textarea);
        textarea.focus();
    }

    textereaProps(item) {
        item.style.height = "auto";
        item.style.padding = "5px";
        item.style["border-radius"] = "3px";
        item.style.resize = "none";
        item.style.height = `${item.scrollHeight}px`;
        item.style["overflow-y"] = (item.scrollHeight > 150) ? "scroll" : "hidden";
    }

    itemLoseFocus() {
        const element = document.getElementById("moreItemsListId");
        if (element) {
            element.removeEventListener("keydown", this.itemLoseFocus);
            element.removeEventListener("blur", this.itemLoseFocus);
            element.remove();
        }
    }
}
